import {Button, Checkbox, Indicator, Menu, Skeleton} from '@mantine/core';
import classNames from 'classnames';
import Head from 'next/head';
import {useRouter} from 'next/router';
import React from 'react';
import {MdFilterAlt, MdUnfoldMore} from 'react-icons/md';
import {TITLE_SUFFIX} from '../../../config/constants';
import {GoogleAdBannerName} from '../../../config/constants/googleAdBanner';
import {GTAG_LOCATION} from '../../../config/constants/gtag';
import {GSSKT_HOST} from '../../../config/constants/hosts';
import {HOME, REPORTS} from '../../../config/constants/routes';
import {Crumb} from '../../../core/entities/reports/constants';
import {useReportsCount} from '../../../core/repositories/reportsRepository';
import {useAuth} from '../../../core/usecases/auth/AuthContext';
import {GoogleAdBanner} from '../../../shared/components/Banner/GoogleAdBanner';
import {SpHeaderBanner} from '../../../shared/components/Banner/SpHeaderBanner';
import {SpTopBottomBanner} from '../../../shared/components/Banner/SpTopBottomBanner';
import {useGoogleAdBanner} from '../../../shared/services/useGoogleAdBanner';
import {useMediaQuery} from '../../../shared/services/useMediaQuery';
import {useWindowSize} from '../../../shared/services/useWindowSize';
import {
  type UseHandleReportsReturnType,
  useHandleReports,
} from '../usecase/useHandleReports';
import {CompanyNameSearch} from './CompanyNameSearch';
import {ReportCountCard} from './Container/Presenter/ReportCountCard';
import {FloatBanner} from './FloatBanner';
import {ReportsHeader} from './Header';
import {IndustrySelector} from './IndustrySelector';

type Props = {
  pageTitle: string;
  description: string;
  handleReportParams: UseHandleReportsReturnType;
  handleChangeReportType: (value: string[]) => void;
  handleChangeDepartment: (value: string[]) => void;
};

const ReportTopContent: React.FC<Props> = ({
  pageTitle,
  description,
  handleReportParams: {
    industryCategories,
    filterItems,
    reportsCount,
    departmentMaster,
    searchWord,
    autoCompleteData,
    handleSearchInputChange,
    reportTypes,
  },
  handleChangeDepartment,
  handleChangeReportType,
}) => {
  // DFPバナーのセット
  const googleAdBannerNames = [
    GoogleAdBannerName.RICH_DESKTOP_HEADER,
    GoogleAdBannerName.A_TOP_PAGE_1,
  ];
  useGoogleAdBanner(googleAdBannerNames);

  const {isPC} = useMediaQuery();
  const {width: windowWidth} = useWindowSize();

  const crumbs = [
    {label: Crumb.HOME, href: HOME},
    {label: Crumb.REPORTS, href: REPORTS},
  ];

  const companyReportCountList = reportsCount?.ok
    ? reportsCount.value.flatMap((category) => category.companyList)
    : [];

  const {status} = useAuth();

  return (
    <>
      <div className="h-[152px] bg-black-5 hidden pc:block">
        <GoogleAdBanner
          googleAdBannerName={GoogleAdBannerName.RICH_DESKTOP_HEADER}
        />
      </div>
      {status === 'authenticated' && (
        <SpHeaderBanner
          location={GTAG_LOCATION.COMPANY_REPORTS}
          className="p-20 pc:hidden"
        />
      )}

      <ReportsHeader
        pageTitle={pageTitle}
        description={description}
        crumbs={crumbs}
      />

      <div className="hidden pc:block h-68">
        <IndustrySelector
          categories={
            industryCategories?.ok
              ? industryCategories.value.industryCategories
              : undefined
          }
        />
      </div>
      <div className="w-full pc:w-[1200px]">
        <div
          className={classNames(
            'h-68 pc:h-[60px] px-16 py-12 bg-white rounded-[20px] shadow-card justify-between mx-16 pc:mt-8 mt-16 pc:flex pc:gap-8 pc:mx-0',
          )}
        >
          <div className="pc:hidden">
            <div className="h-40">
              <IndustrySelector
                categories={
                  industryCategories?.ok
                    ? industryCategories.value.industryCategories
                    : undefined
                }
              />
            </div>
            <div className="h-36 mt-8">
              <CompanyNameSearch
                searchWord={searchWord}
                autoCompleteData={autoCompleteData}
                handleSearchInputChange={handleSearchInputChange}
                isPC={isPC}
              />
            </div>
          </div>

          <div className="h-36 flex my-4 pc:gap-12">
            <Menu>
              <Menu.Target>
                <Button
                  className={classNames('rounded-[20px] border-none', {
                    'bg-background text-primary': isPC,
                    'bg-white text-black px-[2px]': !isPC,
                  })}
                  variant="outline"
                >
                  <div
                    className={classNames('flex', {
                      'gap-8': isPC,
                    })}
                  >
                    {filterItems.reportType.length > 0 ? (
                      <Indicator size={6} color="red">
                        <MdFilterAlt size={20} />
                      </Indicator>
                    ) : (
                      <MdFilterAlt size={20} />
                    )}
                    <span
                      className={classNames({
                        'p-4': isPC,
                        'pt-4': !isPC,
                      })}
                    >
                      レポートの種類
                    </span>
                    <MdUnfoldMore size={20} />
                  </div>
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Checkbox.Group
                  value={filterItems.reportType.map(String)}
                  onChange={handleChangeReportType}
                >
                  {reportTypes.types.map((type) => (
                    <Checkbox
                      key={type.id}
                      className="p-4"
                      styles={{
                        labelWrapper: {
                          width: '100%',
                          userSelect: 'none',
                        },
                      }}
                      value={String(type.id)}
                      label={
                        <span className="w-full mx-auto">{type.name}</span>
                      }
                    />
                  ))}
                </Checkbox.Group>
              </Menu.Dropdown>
            </Menu>

            <Menu middlewares={{flip: false, shift: false}}>
              <Menu.Target>
                <Button
                  className={classNames('rounded-[20px] border-none', {
                    'bg-background text-primary': isPC,
                    'bg-white text-black px-[2px]': !isPC,
                  })}
                  variant="outline"
                >
                  <div
                    className={classNames('flex', {
                      'gap-8': isPC,
                    })}
                  >
                    {filterItems.department.length > 0 ? (
                      <Indicator size={6} color="red">
                        <MdFilterAlt size={20} />
                      </Indicator>
                    ) : (
                      <MdFilterAlt size={20} />
                    )}
                    <span
                      className={classNames({
                        'p-4': isPC,
                        'pt-4': !isPC,
                      })}
                    >
                      部門・職種
                    </span>
                    <MdUnfoldMore size={20} />
                  </div>
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Checkbox.Group
                  value={filterItems.department.map(String)}
                  onChange={handleChangeDepartment}
                >
                  {departmentMaster?.ok &&
                    departmentMaster.value.map((department) => (
                      <Checkbox
                        key={department.id}
                        className="p-4"
                        styles={{
                          labelWrapper: {
                            width: '100%',
                            userSelect: 'none',
                          },
                        }}
                        value={String(department.id)}
                        checked={departmentMaster?.value
                          .map((dep) => dep.id)
                          .includes(department.id)}
                        label={department.name}
                      />
                    ))}
                </Checkbox.Group>
              </Menu.Dropdown>
            </Menu>
          </div>

          <div className="hidden pc:block w-1/2">
            <CompanyNameSearch
              searchWord={searchWord}
              autoCompleteData={autoCompleteData}
              handleSearchInputChange={handleSearchInputChange}
              isPC={isPC}
            />
          </div>
          <div className="hidden pc:block" />
        </div>
      </div>
      <div
        className={classNames(
          'w-full mt-16 grid pc:w-[1200px] grid-cols-1 pc:grid-cols-3 px-16 pc:px-0 gap-8 pc:gap-16',
        )}
      >
        {reportsCount?.ok &&
          companyReportCountList.map((company, i) => (
            <React.Fragment key={company.companyID}>
              <ReportCountCard company={company} />
              {i === 3 && (
                <div className="pc:hidden flex justify-center py-8">
                  <SpTopBottomBanner type={'top'} width={windowWidth} />
                </div>
              )}
              {i === 6 && (
                <div className="pc:hidden flex justify-center py-8">
                  <SpTopBottomBanner type={'bottom'} width={windowWidth} />
                </div>
              )}
            </React.Fragment>
          ))}
        {!reportsCount?.ok &&
          Array.from({length: 30}).map((_, i) => (
            <div key={i} className="pc:h-[110px] pc:inline p-16">
              <Skeleton height={30} className="p-4" />
              <div className="h-20" />

              <Skeleton height={30} className="p-4" />
            </div>
          ))}
      </div>

      <FloatBanner />
    </>
  );
};

export const ReportTopPage: React.FC = () => {
  const router = useRouter();
  const currentUrl = `${GSSKT_HOST}${decodeURI(router.asPath.replace(/\?.*$/, ''))}`;

  const handleReportParams = useHandleReports();
  const {filterItems, setFilterItems} = handleReportParams;
  const pageTitle = 'インターン・本選考のES・選考体験記一覧';

  const handleChangeReportType = (value: string[]) => {
    const newFilterItems = {
      ...filterItems,
      reportType: value.map(Number),
    };
    setFilterItems(newFilterItems);
  };

  const handleChangeDepartment = (value: string[]) => {
    const newFilterItems = {
      ...filterItems,
      department: value.map(Number),
    };
    setFilterItems(newFilterItems);
  };

  const {data: reportsCountByCompany} = useReportsCount({
    queryParams: {
      types: [],
      industryCategoryIDs: [],
      departmentMasterIDs: [],
    },
  });
  const reportCountSum = reportsCountByCompany?.ok
    ? reportsCountByCompany.value.reduce(
        (acc1, cur1) =>
          acc1 +
          cur1.companyList.reduce(
            (acc2, cur2) =>
              acc2 +
              cur2.counts.entrysheetCount +
              cur2.counts.internCount +
              cur2.counts.selectionCount,
            0,
          ),
        0,
      )
    : 0;

  const description = `厳選されたトップ企業の本選考・インターンの最終面接受験者・内定者による体験談・ESを職種別・卒年別に合計${reportCountSum}件掲載。選考突破を目指して、志望企業の選考の実体験データを確認しよう。`;

  return (
    <>
      <Head>
        <title key="title">{pageTitle + TITLE_SUFFIX}</title>
        <meta key="description" name="description" content={description} />
        <meta key="og:title" property="og:title" content="ES・体験記" />
        <meta key="og:url" property="og:url" content={currentUrl} />
        <meta
          key="og:description"
          name="og:description"
          content={description}
        />
        <meta
          name="keywords"
          content="外資,就活,インターンシップ,新卒採用,会社説明会,コラム,ES,エントリーシート・体験記,体験談,投資銀行,コンサル,商社,エンジニア,SWE,内定,本選考"
        />
        <link rel="canonical" href={currentUrl} key="canonical" />
        <meta
          name="viewport"
          content="width=device-width,initial-scale=1.0,maximum-scale=1.0"
        />
      </Head>

      <ReportTopContent
        pageTitle={pageTitle}
        description={description}
        handleReportParams={handleReportParams}
        handleChangeDepartment={handleChangeDepartment}
        handleChangeReportType={handleChangeReportType}
      />
    </>
  );
};
